import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';

export interface AdvancedSettingsData {
  monthlyCost: number;
  retentionRate: number;
}

@Component({
  selector: 'app-advanced-settings-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatInputModule,
    MatFormFieldModule,
    MatIconModule
  ],
  template: `
    <div class="dialog-container">
      <div class="dialog-header">
        <h2 mat-dialog-title>Advanced Settings</h2>
        <button mat-icon-button mat-dialog-close class="close-btn">
          <mat-icon>close</mat-icon>
        </button>
      </div>
      
      <div mat-dialog-content class="dialog-content">
        <div class="settings-grid">
          <div class="setting-group">
            <label for="monthlyCost">Monthly Cost per Customer</label>
            <div class="currency-input">
              <span class="currency-symbol">$</span>
              <input 
                type="number" 
                id="monthlyCost" 
                [(ngModel)]="data.monthlyCost"
                min="0"
                class="setting-input"
                placeholder="15">
            </div>
            <p class="setting-description">The monthly operational cost per customer</p>
          </div>
          
          <div class="setting-group">
            <label for="retentionRate">Customer Retention Rate</label>
            <div class="percentage-input">
              <input 
                type="number" 
                id="retentionRate" 
                [(ngModel)]="data.retentionRate"
                min="0"
                max="100"
                class="setting-input"
                placeholder="85">
              <span class="percentage-symbol">%</span>
            </div>
            <p class="setting-description">Percentage of customers retained each year</p>
          </div>
        </div>
      </div>
      
      <div mat-dialog-actions class="dialog-actions">
        <button mat-button mat-dialog-close class="cancel-btn">Cancel</button>
        <button mat-raised-button [mat-dialog-close]="data" class="save-btn">Save Settings</button>
      </div>
    </div>
  `,
  styles: [`
    .dialog-container {
      width: 500px;
      max-width: 90vw;
    }
    
    .dialog-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 20px;
      padding-bottom: 15px;
      border-bottom: 2px solid #f0f0f0;
    }
    
    .dialog-header h2 {
      margin: 0;
      color: #1a522e;
      font-size: 1.5rem;
    }
    
    .close-btn {
      color: #666;
    }
    
    .dialog-content {
      padding: 0;
    }
    
    .settings-grid {
      display: flex;
      flex-direction: column;
      gap: 30px;
    }
    
    .setting-group {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }
    
    .setting-group label {
      font-size: 1.1rem;
      font-weight: 600;
      color: #1a522e;
    }
    
    .setting-input {
      padding: 15px;
      font-size: 1.2rem;
      font-weight: 600;
      text-align: center;
      border: 2px solid #779e4f;
      border-radius: 8px;
      background: white;
      color: #1a522e;
      transition: all 0.3s ease;
      width: 150px;
    }
    
    .setting-input:focus {
      outline: none;
      border-color: #1a522e;
      box-shadow: 0 0 10px rgba(26, 82, 46, 0.2);
    }
    
    .currency-input, .percentage-input {
      position: relative;
      display: flex;
      align-items: center;
    }
    
    .currency-symbol {
      position: absolute;
      left: 15px;
      font-size: 1.2rem;
      font-weight: 700;
      color: #779e4f;
      z-index: 1;
    }
    
    .currency-input .setting-input {
      padding-left: 40px;
    }
    
    .percentage-symbol {
      position: absolute;
      right: 15px;
      font-size: 1.2rem;
      font-weight: 700;
      color: #779e4f;
      z-index: 1;
    }
    
    .percentage-input .setting-input {
      padding-right: 40px;
    }
    
    .setting-description {
      font-size: 0.9rem;
      color: #666;
      margin: 0;
      font-style: italic;
    }
    
    .dialog-actions {
      display: flex;
      justify-content: flex-end;
      gap: 15px;
      margin-top: 30px;
      padding-top: 20px;
      border-top: 2px solid #f0f0f0;
    }
    
    .cancel-btn {
      color: #666;
    }
    
    .save-btn {
      background: linear-gradient(135deg, #1a522e 0%, #779e4f 100%);
      color: white;
    }
    
    @media (max-width: 600px) {
      .dialog-container {
        width: 100%;
      }
      
      .settings-grid {
        gap: 20px;
      }
      
      .setting-input {
        width: 120px;
      }
    }
  `]
})
export class AdvancedSettingsDialogComponent {
  constructor(
    public dialogRef: MatDialogRef<AdvancedSettingsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AdvancedSettingsData
  ) {}
}